import React from 'react';
import {
  AuditSpotAuditSpotMediaUpload200,
  getApiV2Client,
  getAuditSpotAuditSpotMediaUploadUrl,
  stationsStationsGroupsListForStation,
} from '@mi-front/api-v2';
import { AuditAudio } from '@mi-front/core';
import { AxiosProgressEvent } from 'axios';
import { UploadAction } from './reducer';

interface FileWithConfig {
  readonly file: File;
  readonly audioId?: string;
}

/**
 * Uploads spot media to api-v2 with multipart FormData.
 * Uses the shared client directly so `onUploadProgress` is preserved
 * (generated Kubb `RequestConfig` does not expose it).
 */
async function uploadSpotMedia(args: {
  file: File;
  customId?: string;
  stationId?: string;
  stationsGroupId?: string;
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void;
}): Promise<AuditSpotAuditSpotMediaUpload200> {
  const formData = new FormData();
  formData.append('file', args.file);
  formData.append('type', args.stationsGroupId ? 'STATIONS_GROUP_AUDIO' : 'STATION_AUDIO');

  if (args.stationId) {
    formData.append('stationId', args.stationId);
  }

  if (args.stationsGroupId) {
    formData.append('stationsGroupId', args.stationsGroupId);
  }

  if (args.customId) {
    formData.append('customId', args.customId);
  }

  const { data } = await getApiV2Client().request<AuditSpotAuditSpotMediaUpload200>(
    'POST',
    getAuditSpotAuditSpotMediaUploadUrl().url,
    {
      data: formData,
      onUploadProgress: args.onUploadProgress,
    },
  );

  return data;
}

/**
 * Uploads one or more audio files for a station (or its groups) via api-v2 media upload.
 */
export async function uploadFiles(args: {
  stationId: string;
  files: readonly FileWithConfig[];
  dispatch: React.Dispatch<UploadAction>;
}) {
  const { stationId, files, dispatch } = args;

  dispatch({
    type: 'ALL_STARTED',
  });

  for (let i = 0; i < files.length; i++) {
    const fileWithConfig = files[i];

    dispatch({
      type: 'UPLOAD_STARTED',
      payload: {
        index: i,
      },
    });

    try {
      const groups = await stationsStationsGroupsListForStation(stationId);

      const onProgress = (progress: number) =>
        dispatch({
          type: 'UPLOAD_PROGRESS',
          payload: {
            index: i,
            progress,
          },
        });

      const data = groups.length
        ? await uploadAudioToGroups({
            fileWithConfig,
            groupIds: groups.map((g) => g.id),
            onProgress,
          })
        : await uploadAudioToStation({
            fileWithConfig,
            stationId: stationId,
            onProgress,
          });

      dispatch({
        type: 'UPLOAD_COMPLETED',
        payload: {
          index: i,
          overridden: data?._overridden as AuditAudio | undefined,
        },
      });
    } catch (error) {
      dispatch({
        type: 'UPLOAD_ERROR',
        payload: {
          index: i,
          error: error instanceof Error ? error : new Error(String(error)),
        },
      });
    }
  }

  dispatch({
    type: 'ALL_COMPLETED',
  });
}

function uploadAudioToStation({
  fileWithConfig,
  stationId,
  onProgress,
}: {
  fileWithConfig: {
    file: File;
    audioId?: string;
  };
  stationId: string;
  onProgress: (progress: number) => void;
}) {
  return uploadSpotMedia({
    file: fileWithConfig.file,
    customId: fileWithConfig.audioId?.trim(),
    stationId,
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!);
      onProgress(percentCompleted);
    },
  });
}

async function uploadAudioToGroups({
  fileWithConfig,
  groupIds,
  onProgress,
}: {
  fileWithConfig: {
    file: File;
    audioId?: string;
  };
  groupIds: string[];
  onProgress: (progress: number) => void;
}) {
  let data: AuditSpotAuditSpotMediaUpload200 | undefined;

  for (const groupId of groupIds) {
    const groupPercentCompleted = 1 / groupIds.length;
    const percentAtStart = groupIds.indexOf(groupId) * groupPercentCompleted;

    data = await uploadSpotMedia({
      file: fileWithConfig.file,
      customId: fileWithConfig.audioId?.trim(),
      stationsGroupId: groupId,
      onUploadProgress: (progressEvent) => {
        const filePercentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total!,
        );

        onProgress(percentAtStart + groupPercentCompleted * filePercentCompleted);
      },
    });
  }

  return data;
}
